import React, { useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Spin, Tag, Tabs, message } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { getTaskById, deleteTask } from '@/api/tasks';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import type { Task } from '@/types/employee/task';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// Type for delete error that matches DeleteConfirmationModal interface
type DeleteError = Error & { 
  dependencies?: Array<{ 
    type: string; 
    items: Array<{ id: string; name?: string; number?: string; title?: string }> 
  }>; 
  blockName?: string; 
  unitNumber?: string; 
  amenityName?: string 
};

const TaskDetailsPage: React.FC = () => {
  const { t } = useTranslation(['tasks', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  
  // State for delete confirmation
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<DeleteError | undefined>(undefined);
  
  // Get task from navigation state or fetch from API
  const state = location.state as { task: Task; from: string } | null;
  
  const { data: task, isLoading } = useQuery({
    queryKey: ['task', id],
    queryFn: () => getTaskById(id!, firestoreUser!),
    enabled: !!id && !state?.task && !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteTask(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('tasks:deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setOpenDeleteDialog(false);
      setDeleteError(undefined);
      navigate('/employee/tasks');
    },
    onError: (error: Error) => {
      setDeleteError(error as DeleteError);
      // Don't close modal on error - let DeleteConfirmationModal handle it
    }
  });

  // Use state data if available, otherwise use fetched data
  const displayTask = state?.task || task;

  // Loading state - only show if no state data is available
  if (isLoading && !state?.task) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayTask) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Title level={4}>{t('tasks:notFound')}</Title>
          <Button type="primary" onClick={() => navigate('/employee/tasks')}>
            {t('common:backToList')}
          </Button>
        </div>
      </Card>
    );
  }

  const getStatusTagColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'in_progress': return 'blue';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const getPriorityTagColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'green';
      case 'medium': return 'blue';
      case 'high': return 'orange';
      case 'urgent': return 'red';
      default: return 'default';
    }
  };

  const getTypeTagColor = (type: string) => {
    switch (type) {
      case 'maintenance': return 'blue';
      case 'cleaning': return 'green';
      case 'inspection': return 'purple';
      case 'security': return 'orange';
      case 'other': return 'default';
      default: return 'default';
    }
  };

  const handleEdit = () => {
    navigate(`/employee/tasks/${displayTask.id}/edit`, { 
      state: { 
        task: displayTask,
        from: 'details'
      } 
    });
  };

  const handleDelete = useCallback(() => {
    setDeleteError(undefined);
    setOpenDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (displayTask.id) {
      deleteMutation.mutate(displayTask.id);
    }
  }, [displayTask.id, deleteMutation]);

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteError(undefined);
  };

  const handleErrorDismiss = () => {
    setDeleteError(undefined);
  };

  const handleBack = () => {
    if (state?.from === 'list') {
      navigate(-1);
    } else {
      navigate('/employee/tasks');
    }
  };

  const handleDownload = (attachment: any) => {
    const url = attachment.url;
    const fileName = attachment.name;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const tabItems = [
    {
      key: 'details',
      label: t('common:details'),
      children: (
        <div>
          {/* Basic Information */}
          <Descriptions title={t('tasks:basicInformation')} bordered column={2}>
            <Descriptions.Item label={t('tasks:title')}>
              {displayTask.title}
            </Descriptions.Item>
            <Descriptions.Item label={t('tasks:statusLabel')}>
              <Tag color={getStatusTagColor(displayTask.status)}>
                {t(`tasks:status.${displayTask.status}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('tasks:typeLabel')}>
              <Tag color={getTypeTagColor(displayTask.type)}>
                {t(`tasks:types.${displayTask.type}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('tasks:priorityLabel')}>
              <Tag color={getPriorityTagColor(displayTask.priority)}>
                {t(`tasks:priorities.${displayTask.priority}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('tasks:assignedTo')}>
              {displayTask.assignedTo.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('tasks:assignedBy')}>
              {displayTask.assignedBy.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('tasks:dueDate')}>
              {dayjs(displayTask.dueDate).format('MMM DD, YYYY')}
            </Descriptions.Item>
            {displayTask.completedAt && (
              <Descriptions.Item label={t('tasks:completedAt')}>
                {dayjs(displayTask.completedAt).format('MMM DD, YYYY HH:mm')}
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* Description Section */}
          <div style={{ marginTop: 24 }}>
            <Descriptions title={t('tasks:description')} bordered column={1}>
              <Descriptions.Item>
                <Text>{displayTask.description}</Text>
              </Descriptions.Item>
            </Descriptions>
          </div>

          {/* Location Information */}
          {(displayTask.amenity || displayTask.apartmentUnit || displayTask.asset) && (
            <div style={{ marginTop: 24 }}>
              <Descriptions title={t('tasks:locationInformation')} bordered column={2}>
                {displayTask.amenity && (
                  <Descriptions.Item label={t('tasks:amenity')}>
                    {displayTask.amenity.name}
                  </Descriptions.Item>
                )}
                {displayTask.apartmentUnit && (
                  <Descriptions.Item label={t('tasks:apartmentUnit')}>
                    {displayTask.apartmentUnit.name}
                  </Descriptions.Item>
                )}
                {displayTask.asset && (
                  <Descriptions.Item label={t('tasks:asset')}>
                    {displayTask.asset.name}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          )}

          {/* Attachments Section */}
          {displayTask.attachments && displayTask.attachments.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <Descriptions title={t('tasks:attachments')} bordered column={1}>
                {displayTask.attachments.map((attachment: any, index: number) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(attachment)}
                    >
                      {attachment.name}
                    </Button>
                  </div>
                ))}
              </Descriptions>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'activity',
      label: t('common:activityLog'),
      children: (
        <div>
          {displayTask.activityRecords && displayTask.activityRecords.length > 0 ? (
            <div>
              {displayTask.activityRecords.map((record: any, index: number) => (
                <div key={index} style={{ marginBottom: '16px', padding: '12px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text strong>{record.type}</Text>
                    <Text type="secondary">{dayjs(record.timestamp).format('YYYY-MM-DD HH:mm')}</Text>
                  </div>
                  <Text>{record.performedBy.name}</Text>
                  {record.changedFields && (
                    <div style={{ marginTop: '8px' }}>
                      {Object.entries(record.changedFields).map(([field, values]: [string, any]) => (
                        <div key={field} style={{ fontSize: '12px', color: '#666' }}>
                          {field}: {values.oldValue} → {values.newValue}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              {t('common:noActivityLog')}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', padding: 24 }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              {t('common:back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              {t('tasks:taskDetails')} - {displayTask.title}
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Card>
        <Tabs items={tabItems} />
      </Card>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={openDeleteDialog}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('common:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
};

export default TaskDetailsPage; 